import z from 'zod';
import { intakeItemSchema } from './intakeItem';

export enum IntakeItemDocumentKind {
  PDF = 'pdf',
  IMAGE = 'image',
  TABULAR = 'tabular',
  UNSUPPORTED = 'unsupported',
}

export enum IntakeItemDocumentOcrType {
  EKG = 'ekg',
  RX = 'rx',
  UNKNOWN = 'unknown',
}

export enum IntakeItemDocumentClaimStatus {
  CREATED = 'created',
  SKIPPED = 'skipped',
  FAILED = 'failed',
}

export enum IntakeItemDocumentConfidenceMethod {
  ROW_AVERAGE = 'row_average',
}

export enum IntakeItemDocumentStatus {
  QUEUED = 'queued',
  PROCESSING = 'processing',
  WAITING_ON_TEXTRACT = 'waiting_on_textract',
  COMPLETED = 'completed',
  COMPLETED_WITH_ERRORS = 'completed_with_errors',
  FAILED = 'failed',
  SKIPPED = 'skipped',
}

/** 1-based page; normalized top-left origin, positive dimensions within the page. */
export const ocrCoordinatesSchema = z
  .object({
    page: z.number().int().positive(),
    left: z.number().min(0).max(1),
    top: z.number().min(0).max(1),
    width: z.number().positive().max(1),
    height: z.number().positive().max(1),
  })
  .refine(
    (box) => box.left + box.width <= 1 && box.top + box.height <= 1,
    'Source region must fit within its page'
  );

export const ocrDocumentSchema = z.object({
  key: z.string().min(1),
});

export const cellMetadataSchema = z.object({
  coordinates: z.array(ocrCoordinatesSchema),
  blockIDs: z.array(z.string()),
  inherited: z.boolean().optional(),
  sourceRowID: z.string().optional(),
});

export const parsedOCRRowMetadataSchema = z.object({
  sourceRowID: z.string().min(1),
  coordinates: z.array(ocrCoordinatesSchema),
  blockIDs: z.array(z.string()).optional(),
  tableID: z.string().optional(),
  tableIndex: z.number().int().nonnegative().optional(),
  rowNumber: z.number().int().positive().optional(),
  geometryStatus: z.enum(['available', 'partial', 'unavailable']).optional(),
  cells: z.record(z.string(), cellMetadataSchema).optional(),
});

export const pdfClaimSourceSchema = z.object({
  kind: z.literal('pdf'),
  coordinates: z.array(ocrCoordinatesSchema),
});

export const workbookClaimSourceSchema = z.object({
  kind: z.literal('workbook'),
  sheetName: z.string(),
  sheetIndex: z.number().int().nonnegative(),
  rowNumber: z.number().int().positive(),
});

export const csvClaimSourceSchema = z.object({
  kind: z.literal('csv'),
  recordNumber: z.number().int().positive(),
});

export const tabularClaimSourceSchema = z.discriminatedUnion('kind', [
  workbookClaimSourceSchema,
  csvClaimSourceSchema,
]);

export const documentClaimSourceSchema = z.discriminatedUnion('kind', [
  pdfClaimSourceSchema,
  workbookClaimSourceSchema,
  csvClaimSourceSchema,
]);

export const documentReviewReasonSchema = z.object({
  code: z.enum([
    'missing_identity',
    'invalid_identity',
    'identity_conflict',
    'ambiguous_identity',
    'invalid_service_date',
    'missing_tariff',
    'unresolved_tariff',
    'missing_diagnosis',
    'unsupported_field',
    'provider_disagreement',
    'ambiguous_layout',
    'dependency_failure',
    'creation_result_mismatch',
  ]),
  field: z.string().optional(),
  severity: z.enum(['warning', 'error']),
  message: z.string(),
});

const claimReviewFields = {
  confidence: z.number().nullable().optional(),
  requiresReview: z.boolean().optional(),
  reviewReasons: z.array(documentReviewReasonSchema).optional(),
  source: documentClaimSourceSchema.optional(),
};

export const parsedOCRMetadataReferenceSchema = z.object({
  schemaVersion: z.literal([1, 2, 3]),
  extractionID: z.string(),
  key: z.string().min(1),
  source: ocrDocumentSchema,
});

const legacyParsedOCRMetadataSchema = z.object({
  schemaVersion: z.literal([1, 2]),
  extractionID: z.string(),
  source: ocrDocumentSchema,
  documentID: z.guid(),
  rows: z.record(
    z.string(),
    parsedOCRRowMetadataSchema.omit({ sourceRowID: true })
  ),
});

export const generatedClaimEvidenceSchema = z.object({
  claimID: z.guid(),
  rowIndex: z.number().int().nonnegative(),
  source: documentClaimSourceSchema,
});

export const generatedClaimOCRMetadataSchema = z.object({
  schemaVersion: z.literal(3),
  extractionID: z.string(),
  source: ocrDocumentSchema,
  documentID: z.guid(),
  claims: z.array(generatedClaimEvidenceSchema),
});

export const tabularRowPreviewSchema = z.object({
  headers: z.array(z.string()),
  values: z.array(z.string()),
});

export const parsedOCRMetadataSchema = z.union([
  legacyParsedOCRMetadataSchema,
  generatedClaimOCRMetadataSchema,
]);
export const parsedOCRMetadataResponseSchema = z.object({
  documentID: z.guid(),
  documentName: z.string(),
  extractionID: z.string().nullable(),
  availability: z.enum(['available', 'unavailable']),
  pdfURL: z.string().nullable(),
  claims: z.array(
    z.object({
      claimID: z.guid(),
      page: z.number().int().nullable(),
      confidence: z.number().nullable().optional(),
      requiresReview: z.boolean(),
      reviewReasons: z.array(documentReviewReasonSchema).optional(),
      source: tabularClaimSourceSchema.optional(),
      coordinates: z.array(ocrCoordinatesSchema),
      tabularRow: tabularRowPreviewSchema.optional(),
    })
  ),
});

export const intakeItemDocumentClaimSummarySchema = z.object({
  ...claimReviewFields,
  claimID: z.guid().nullable(),
  rowIndex: z.number().int(),
  page: z.number().int().nullable(),
  confidence: z.number().nullable(),
  status: z.enum(Object.values(IntakeItemDocumentClaimStatus)),
  reason: z.string().optional(),
});

export const intakeItemDocumentConfidenceSummarySchema = z.object({
  createdBy: z.guid().optional(),
  schemaVersion: z.literal(2).optional(),
  evidence: parsedOCRMetadataReferenceSchema.optional(),
  sourceDocument: ocrDocumentSchema.optional(),
  documentConfidence: z.number().nullable(),
  confidenceMethod: z.enum(Object.values(IntakeItemDocumentConfidenceMethod)),
  rowCount: z.number().int().nonnegative(),
  claimCount: z.number().int().nonnegative(),
  claims: z.array(intakeItemDocumentClaimSummarySchema),
  processedAt: z.string(),
  error: z
    .object({
      message: z.string(),
    })
    .optional(),
});

export const intakeItemDocumentSchema = z.object({
  id: z.guid(),
  intakeItemID: z.guid(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  completedAt: z.coerce.date().nullable().optional(),
  fileName: z.string(),
  contentType: z.string().nullable().optional(),
  kind: z.enum(Object.values(IntakeItemDocumentKind)),
  parser: z.string().nullable().optional(),
  status: z.enum(Object.values(IntakeItemDocumentStatus)),
  textractJobID: z.string().nullable().optional(),
  clientRequestToken: z.string().nullable().optional(),
  confidenceSummary: intakeItemDocumentConfidenceSummarySchema
    .nullable()
    .optional(),
  claimCount: z.number().int().nonnegative(),
});

export const getIntakeItemDocumentsSchema = z.object({
  intakeItemIDs: z
    .array(z.guid('Invalid intake item ID format'))
    .min(1, 'At least one intake item ID is required'),
});

export const claimConfidenceInfoSchema = intakeItemDocumentClaimSummarySchema
  .omit({ claimID: true, status: true, reason: true })
  .extend({
    documentID: z.guid(),
    documentName: z.string(),
    documentStatus: z.string(),
    parser: z.string().nullable(),
    claimStatus: intakeItemDocumentClaimSummarySchema.shape.status,
  });

export const claimConfidenceByIntakeItemIDSchema = z.record(
  z.guid(),
  z.record(z.guid(), claimConfidenceInfoSchema)
);

export const getParsedOCRMetadataSchema = z.object({ documentID: z.guid() });

export const tabularQueueMessageSchema = z.object({
  createdBy: z.guid().optional(),
  intakeItemID: z.guid(),
  documentID: z.guid(),
  bucket: z.string().min(1),
  key: z.string().min(1),
  contentType: z.string().min(1),
});

export const ocrQueueMessageSchema = tabularQueueMessageSchema.extend({
  ...intakeItemSchema.pick({ siteID: true, providerID: true }).shape,
  clientRequestToken: z.string().min(1),
});

export const textractCompletionSchema = z.object({
  JobId: z.string().min(1),
  JobTag: z.string().min(1),
  Status: z.string().min(1),
  DocumentLocation: z
    .object({
      S3ObjectName: z.string().optional(),
      S3Bucket: z.string().optional(),
    })
    .optional(),
});

export const textractNotificationSchema = z.object({
  Message: z.string().min(1),
});

// This is a post-commit correlation check; it preserves the bulk service's ID contract.
export const ocrCreatedClaimsSchema = z.array(
  z.object({ id: z.string().min(1) })
);

export type CellMetadataSchema = z.infer<typeof cellMetadataSchema>;

export type DocumentClaimSource = z.infer<typeof documentClaimSourceSchema>;

export type ParsedOCRMetadataResponse = z.infer<
  typeof parsedOCRMetadataResponseSchema
>;
export type ParsedOCRRowMetadata = z.infer<typeof parsedOCRRowMetadataSchema>;
export type GeneratedClaimEvidence = z.infer<
  typeof generatedClaimEvidenceSchema
>;
export type GeneratedClaimOCRMetadata = z.infer<
  typeof generatedClaimOCRMetadataSchema
>;
export type TabularRowPreview = z.infer<typeof tabularRowPreviewSchema>;

export type DocumentReviewReason = z.infer<typeof documentReviewReasonSchema>;

export type ParsedOCRMetadataReference = z.infer<
  typeof parsedOCRMetadataReferenceSchema
>;
export type ParsedOCRMetadata = z.infer<typeof parsedOCRMetadataSchema>;

export type IntakeItemDocumentClaimSummary = z.infer<
  typeof intakeItemDocumentClaimSummarySchema
>;
export type IntakeItemDocumentConfidenceSummary = z.infer<
  typeof intakeItemDocumentConfidenceSummarySchema
>;
export type IntakeItemDocument = z.infer<typeof intakeItemDocumentSchema>;
export type GetIntakeItemDocumentsRequest = z.infer<
  typeof getIntakeItemDocumentsSchema
>;
export type ClaimConfidenceInfo = z.infer<typeof claimConfidenceInfoSchema>;
export type ClaimConfidenceByIntakeItemID = z.infer<
  typeof claimConfidenceByIntakeItemIDSchema
>;

export type IntakeItemDocumentOcrClaimMetadata = ClaimConfidenceInfo;
export type TextractCompletionMessage = z.infer<
  typeof textractCompletionSchema
>;
export type OCRQueueMessage = z.infer<typeof ocrQueueMessageSchema>;
export type TabularQueueMessage = z.infer<typeof tabularQueueMessageSchema>;
export type OCRCoordinates = z.infer<typeof ocrCoordinatesSchema>;
